import { zodResolver } from '@hookform/resolvers/zod';
import { Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

export type InquiryFormData = {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  product: string;
  quantity?: string;
  message?: string;
  hp_name?: string;
  hp_email?: string;
};

const PRODUCT_OPTIONS = [
  'Fiber Optic Gyroscope',
  'Quartz Accelerometer',
  'MEMS IMU / Sensors',
  'Inertial Navigation System',
  'Other / Not Sure'
];

const QUANTITY_OPTIONS = ['1-10 units', '11-100 units', '101-500 units', '500+ units'];

export function InquiryForm({ compact = false, source = 'website_form' }: { compact?: boolean; source?: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [apiError, setApiError] = useState('');

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().min(2, t('form.errors.nameRequired')),
        email: z.string().email(t('form.errors.emailInvalid')),
        company: z.string().optional(),
        phone: z.string().optional(),
        product: z.string().min(1, t('form.errors.productRequired')),
        quantity: z.string().optional(),
        message: z.string().optional(),
        hp_name: z.string().optional(),
        hp_email: z.string().optional()
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<InquiryFormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: InquiryFormData) => {
    setApiError('');
    // 蜜罐字段：机器人会填写，正常用户不会
    if (data.hp_name || data.hp_email) {
      navigate('/thank-you');
      return;
    }
    const response = await fetch('/api/inquiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, source })
    }).catch(() => null);

    if (!response || !response.ok) {
      setApiError(t('form.errors.serverError'));
      return;
    }

    navigate('/thank-you');
  };

  return (
    <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="hidden" aria-hidden="true">
        <input type="text" tabIndex={-1} autoComplete="off" {...register('hp_name')} />
        <input type="email" tabIndex={-1} autoComplete="off" {...register('hp_email')} />
      </div>
      <div className={`grid gap-4 ${compact ? '' : 'md:grid-cols-2'}`}>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.name')} *</span>
          <input className="field" {...register('name')} autoComplete="name" />
          {errors.name ? <span className="text-sm text-red-600">{errors.name.message}</span> : null}
        </label>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.email')} *</span>
          <input className="field" {...register('email')} autoComplete="email" />
          {errors.email ? <span className="text-sm text-red-600">{errors.email.message}</span> : null}
        </label>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.company')}</span>
          <input className="field" {...register('company')} autoComplete="organization" />
        </label>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.phone')}</span>
          <input className="field" {...register('phone')} placeholder="+86 138 0000 0000" />
        </label>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.productInterest')} *</span>
          <select className="field" {...register('product')}>
            <option value="">{t('form.selectProduct')}</option>
            {PRODUCT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errors.product ? <span className="text-sm text-red-600">{errors.product.message}</span> : null}
        </label>
        <label>
          <span className="mb-1 block text-sm font-semibold">{t('form.quantity')}</span>
          <select className="field" {...register('quantity')}>
            <option value="">{t('form.selectRange')}</option>
            {QUANTITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        <span className="mb-1 block text-sm font-semibold">{t('form.requirements')}</span>
        <textarea className="field min-h-28" {...register('message')} placeholder={t('form.requirementsPlaceholder')} />
      </label>
      {apiError ? <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{apiError}</p> : null}
      <button className="btn-primary w-full sm:w-auto" disabled={isSubmitting}>
        <Send size={18} /> {isSubmitting ? t('actions.submitting') : t('form.submitInquiry')}
      </button>
    </form>
  );
}
